// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Static termination analysis over the rule graph (M2-11).
//
// A rule whose action changes a variable can trigger a rule that changes it back. At runtime
// that is an infinite cascade; the engine's depth guard would cut it off at 8 and the player
// would see a machine that stutters and drops score for reasons nobody can explain. Caught
// here instead, at load, naming the rules that form the loop — which is the difference
// between "your machine is invalid" and "your machine is invalid **because these two rules
// trigger each other**".
//
// The analysis is a reachability graph over *trigger kinds*, not over runtime state. Nodes
// are triggers; an edge means "a rule fired by this trigger has an action that raises that
// one". It deliberately **over-approximates**: it assumes every rule's `when` can pass and
// every mode can be active, because the alternative is deciding conditions it cannot know.
// That makes it sound and slightly conservative — it can refuse a rulesheet that would in
// practice have been fine, and it can never accept one that runs away. For a limit of 8 that
// trade is free; a real rulesheet cascades two or three deep.
//
// **Timers deliberately break the graph.** `startTimer` raises nothing: the expiry arrives in
// a later step as a fresh wave, so it cannot contribute to the depth of this one. A rulesheet
// that ping-pongs through a timer is not a runaway cascade, it is a machine with a heartbeat.

import { MAX_EVENT_DEPTH, parseTrigger, T_MODE_END, T_MODE_START, T_SWITCH, T_TIMER_EXPIRE, T_VAR } from './RuleEngine'
import type { Action, MachineDefinition, MachineMode, MachineRule } from '../machine/types'

export interface TerminationIssue {
  readonly path: string
  readonly code: string
  readonly message: string
  /**
   * `error` refuses the machine; `warning` is advice.
   *
   * The distinction is what makes this analysis usable rather than merely strict. **An
   * unguarded cycle provably diverges** — every rule in it fires unconditionally, so it will
   * run until the depth guard cuts it off, every time. That is an error.
   *
   * **A cycle in which any rule carries a `when` cannot be proven either way**, and the most
   * common pattern in pinball is exactly that shape: fire on a variable changing, then reset
   * the variable. Document 04's own worked example does it — `novaLanesComplete` triggers on
   * `var:novaLanes` and then sets `novaLanes` to empty. It terminates because emptying the
   * set falsifies its own guard, which no reachability analysis can see. Refusing it would
   * have made the format unable to express the rulesheet its own specification describes,
   * so it warns, and the runtime depth guard bounds the case where the author got it wrong.
   */
  readonly severity: 'error' | 'warning'
}

interface EdgeCause {
  readonly rule: string
  /** A rule with a `when`, or a mode hook that only runs while its mode is active. */
  readonly guarded: boolean
}

function key(kind: number, name: string): string {
  return `${kind} ${name}`
}

/**
 * The graph the analysis walks, handed out so it can be drawn (M9-11).
 *
 * **Not a second graph.** 11 §3.2 asks for "a rule-graph view with the depth-8 termination analysis
 * drawn on it — a cycle is shown as a red loop *before* it is ever a validation error", and a view
 * built from a second traversal would be a picture of a different rulesheet. `analyseTermination`
 * builds this and reads it; the Builder draws the same object.
 */
export interface RuleGraph {
  /** Every trigger that fires something or is fired, as `switch:leftRamp` and the like. */
  readonly nodes: readonly string[]
  /** `from → to`, where an edge means "a rule fired by `from` raises `to`". */
  readonly edges: readonly { readonly from: string; readonly to: string; readonly rules: readonly string[]; readonly guarded: boolean }[]
  /** Each cycle as the loop of nodes it runs round, first node repeated at the end. */
  readonly cycles: readonly { readonly loop: readonly string[]; readonly rules: readonly string[]; readonly guarded: boolean }[]
  /** How many waves deep a cascade starting at each node runs. Empty when the graph has a cycle. */
  readonly depth: ReadonlyMap<string, number>
  /** What the engine will actually resolve. Past this, rules silently do not fire. */
  readonly maxDepth: number
}

function label(k: string): string {
  const space = k.indexOf(' ')
  const kind = Number(k.slice(0, space))
  const name = k.slice(space + 1)
  switch (kind) {
    case T_SWITCH: return `switch:${name}`
    case T_VAR: return `var:${name}`
    case T_MODE_START: return `mode:${name}:start`
    case T_MODE_END: return `mode:${name}:end`
    case T_TIMER_EXPIRE: return `timer:${name}:expire`
    default: return name === '' ? 'ball:start/end' : name
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** Triggers one action list can raise, following `startMode` into the mode's own hooks. */
function raisedBy(
  actions: readonly Action[],
  modes: ReadonlyMap<string, MachineMode>,
  seenModes: Set<string>,
  out: Set<string>,
): void {
  for (const action of actions) {
    if ('set' in action || 'add' in action) {
      const spec = ('set' in action ? action['set'] : action['add']) as unknown
      if (isObject(spec) && typeof spec['var'] === 'string') out.add(key(T_VAR, spec['var']))
      continue
    }
    if ('startMode' in action && typeof action['startMode'] === 'string') {
      const id = action['startMode']
      out.add(key(T_MODE_START, id))
      // onStart runs inside the same action, so whatever it raises is raised from here.
      // Guarded against a mode that starts itself, which the cycle pass then reports.
      if (!seenModes.has(id)) {
        seenModes.add(id)
        const mode = modes.get(id)
        if (mode?.onStart) raisedBy(mode.onStart, modes, seenModes, out)
      }
      continue
    }
    if ('endMode' in action) {
      const spec = action['endMode']
      const id = typeof spec === 'string' ? spec : isObject(spec) && typeof spec['id'] === 'string' ? spec['id'] : undefined
      if (id !== undefined) out.add(key(T_MODE_END, id))
    }
    // Every other action is a leaf: a coil, a sound, a score. None raises a rule trigger,
    // which is exactly why the action vocabulary is closed and small.
  }
}

/**
 * Prove the rule graph is acyclic and no cascade can exceed `MAX_EVENT_DEPTH`.
 *
 * Returns the issues found. Empty means the rulesheet provably terminates.
 */
export function analyseTermination(definition: MachineDefinition): TerminationIssue[] {
  return ruleGraphOf(definition).issues
}

/**
 * The rule graph, and what the analysis makes of it.
 *
 * One pass for both, for the reason `analyseWithReachability` gives about the flood fill: the
 * Builder wants the picture *and* the complaints, and computing them separately would be two
 * traversals producing two answers that could differ.
 */
export function ruleGraphOf(definition: MachineDefinition): {
  readonly issues: TerminationIssue[]
  readonly graph: RuleGraph
} {
  const issues: TerminationIssue[] = []
  const declared: readonly MachineRule[] = definition.rules?.rules ?? []
  const modeList = definition.rules?.modes ?? []

  const modes = new Map<string, MachineMode>()
  for (const m of modeList) modes.set(m.id, m)

  // --- build the graph ----------------------------------------------------------
  const edges = new Map<string, Set<string>>()
  /** Which rules justify an edge, so a cycle report can name them and weigh their guards. */
  const because = new Map<string, EdgeCause[]>()

  const addEdges = (from: string, actions: readonly Action[], cause: EdgeCause): void => {
    const raised = new Set<string>()
    raisedBy(actions, modes, new Set<string>(), raised)
    let set = edges.get(from)
    if (!set) {
      set = new Set<string>()
      edges.set(from, set)
    }
    for (const to of raised) {
      set.add(to)
      const list = because.get(`${from}->${to}`)
      if (list) list.push(cause)
      else because.set(`${from}->${to}`, [cause])
    }
  }

  for (const rule of declared) {
    const trigger = parseTrigger(rule.on)
    if (trigger === undefined) continue
    addEdges(key(trigger.kind, trigger.name), rule.do, {
      rule: rule.id,
      guarded: rule.when !== undefined,
    })
  }

  // A mode's shot hooks fire from the switches in its shot set, and completing a mode ends
  // it — both are real edges and both have caused real loops in real rulesheets.
  // A mode hook is inherently guarded: it only runs while its mode is active, and a mode
  // that completes ends itself. That is a real terminating condition the graph cannot see.
  for (const mode of modeList) {
    for (const shot of mode.shots ?? []) {
      const from = key(T_SWITCH, shot)
      if (mode.onShot) addEdges(from, mode.onShot, { rule: `mode ${mode.id} onShot`, guarded: true })
      if (mode.onComplete) addEdges(from, mode.onComplete, { rule: `mode ${mode.id} onComplete`, guarded: true })
      const set = edges.get(from)
      if (set) set.add(key(T_MODE_END, mode.id))
    }
    if (mode.timer !== undefined) {
      const from = key(T_TIMER_EXPIRE, mode.timer)
      if (mode.onTimeout) addEdges(from, mode.onTimeout, { rule: `mode ${mode.id} onTimeout`, guarded: true })
      const set = edges.get(from)
      if (set) set.add(key(T_MODE_END, mode.id))
      else edges.set(from, new Set([key(T_MODE_END, mode.id)]))
    }
  }

  // --- cycles ---------------------------------------------------------------------
  // Iterative depth-first colouring. Nodes are visited in sorted order so that a graph with
  // two independent cycles always reports the same one first.
  const WHITE = 0
  const GREY = 1
  const BLACK = 2
  const colour = new Map<string, number>()
  const nodes = [...edges.keys()].sort()
  for (const n of nodes) colour.set(n, WHITE)

  const stack: string[] = []
  const reported = new Set<string>()
  const cycles: { loop: readonly string[]; rules: readonly string[]; guarded: boolean }[] = []

  const visit = (node: string): void => {
    colour.set(node, GREY)
    stack.push(node)
    const next = [...(edges.get(node) ?? [])].sort()
    for (const to of next) {
      const c = colour.get(to) ?? WHITE
      if (c === GREY) {
        const at = stack.indexOf(to)
        const loop = stack.slice(at < 0 ? 0 : at).concat(to)
        const signature = loop.join('|')
        if (!reported.has(signature)) {
          reported.add(signature)
          const rules = new Set<string>()
          // Unguarded until proven otherwise: a loop is only provably divergent when every
          // rule in it fires unconditionally.
          let anyGuarded = false
          for (let i = 0; i + 1 < loop.length; i++) {
            for (const cause of because.get(`${loop[i]}->${loop[i + 1]}`) ?? []) {
              rules.add(cause.rule)
              if (cause.guarded) anyGuarded = true
            }
          }
          const named = [...rules].sort().join(', ') || '(mode hooks)'
          const path = `${loop.map(label).join(' → ')}`
          cycles.push({ loop: loop.map(label), rules: [...rules].sort(), guarded: anyGuarded })
          issues.push(
            anyGuarded
              ? {
                  path: 'rules.rules',
                  code: 'rule-cycle-guarded',
                  severity: 'warning',
                  message:
                    `${named} form a feedback loop (${path}), broken only by a \`when\` this ` +
                    'analysis cannot evaluate. That is the normal shape for "fire on a change, ' +
                    'then reset it", and it is fine as long as the action really does falsify ' +
                    `the guard. If it does not, the cascade will be cut off at depth ${MAX_EVENT_DEPTH} ` +
                    'and the rules after it will silently not fire.',
                }
              : {
                  path: 'rules.rules',
                  code: 'rule-cycle',
                  severity: 'error',
                  message:
                    `${named} trigger each other in a loop with nothing to stop it (${path}). ` +
                    'Every rule in this cycle fires unconditionally, so it will run until the ' +
                    'engine cuts it off, every time. Add a `when` that the loop falsifies, or ' +
                    'route one step through a timer — a timer expiry begins a new wave.',
                },
          )
        }
        continue
      }
      if (c === WHITE) visit(to)
    }
    stack.pop()
    colour.set(node, BLACK)
  }

  for (const n of nodes) if ((colour.get(n) ?? WHITE) === WHITE) visit(n)

  /** The edges, labelled, for anything that draws them. */
  const drawnEdges = [...edges.entries()].flatMap(([from, tos]) =>
    [...tos].sort().map((to) => {
      const causes = because.get(`${from}->${to}`) ?? []
      return {
        from: label(from),
        to: label(to),
        rules: [...new Set(causes.map((cause) => cause.rule))].sort(),
        guarded: causes.length > 0 && causes.every((cause) => cause.guarded),
      }
    }),
  )
  // **Labelled once, from the raw keys.** The first draft built this set from `nodes` *and* the
  // already-labelled edge targets, then mapped `label` over the lot — so every node that was both a
  // source and a target appeared twice, because labelling a label is the identity. React said so, at
  // thirty duplicate keys on the shipped machine. Targets are collected as raw keys here.
  const rawNodes = new Set<string>(nodes)
  for (const tos of edges.values()) for (const to of tos) rawNodes.add(to)
  const drawnNodes = [...rawNodes].sort().map(label)

  // A cyclic graph has no finite longest path, so the depth pass below cannot run on one —
  // and that is true of a guarded cycle too, even though it is only a warning. The cycle is
  // the more useful report in either case.
  if (issues.length > 0) {
    return {
      issues,
      graph: {
        nodes: drawnNodes,
        edges: drawnEdges,
        cycles,
        depth: new Map<string, number>(),
        maxDepth: MAX_EVENT_DEPTH,
      },
    }
  }

  // --- depth ---------------------------------------------------------------------------
  const longest = new Map<string, number>()
  const depthOf = (node: string): number => {
    const cached = longest.get(node)
    if (cached !== undefined) return cached
    longest.set(node, 0) // acyclic by now, so this is only ever read after it is finalised
    let best = 0
    for (const to of edges.get(node) ?? []) {
      const d = 1 + depthOf(to)
      if (d > best) best = d
    }
    longest.set(node, best)
    return best
  }

  const drawnDepth = new Map<string, number>()
  for (const n of nodes) {
    const d = depthOf(n)
    drawnDepth.set(label(n), d + 1)
    if (d + 1 > MAX_EVENT_DEPTH) {
      issues.push({
        path: 'rules.rules',
        code: 'rule-cascade-too-deep',
        severity: 'error',
        message:
          `${label(n)} starts a cascade ${d + 1} waves deep; the engine resolves at most ` +
          `${MAX_EVENT_DEPTH}. Beyond that the remaining rules would silently not fire.`,
      })
    }
  }

  return {
    issues,
    graph: {
      nodes: drawnNodes,
      edges: drawnEdges,
      cycles,
      depth: drawnDepth,
      maxDepth: MAX_EVENT_DEPTH,
    },
  }
}
